import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthProvider';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    token: '',
    new_password: '',
    confirm_password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const auth = useAuth();

  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      setFormData(prev => ({ ...prev, token }));
    } else {
      setStatus({
        type: 'error',
        message: 'Invalid or missing reset token. Please request a new password reset link.'
      });
    }
  }, [searchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear errors when user starts typing again
    if (status.type === 'error') {
      setStatus({ type: '', message: '' });
    }
  };

  const validatePasswords = () => {
    if (formData.new_password.length < 8) {
      setStatus({
        type: 'error',
        message: 'Password must be at least 8 characters long'
      });
      return false;
    }
    
    // Add stronger password validation
    const hasUpperCase = /[A-Z]/.test(formData.new_password);
    const hasLowerCase = /[a-z]/.test(formData.new_password);
    const hasNumbers = /\d/.test(formData.new_password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(formData.new_password);
    
    if (!(hasUpperCase && hasLowerCase && hasNumbers)) {
      setStatus({
        type: 'error',
        message: 'Password must contain uppercase letters, lowercase letters, and numbers'
      });
      return false;
    }
    
    if (formData.new_password !== formData.confirm_password) {
      setStatus({
        type: 'error',
        message: 'Passwords do not match'
      });
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validatePasswords()) return;

    setIsLoading(true);
    setStatus({ type: '', message: '' }); // Clear previous status
    
    try {
      // Set timeout to handle network issues
      const timeoutId = setTimeout(() => {
        if (isLoading) {
          setIsLoading(false);
          setStatus({
            type: 'error',
            message: 'Request timed out. Please check your internet connection and try again.'
          });
        }
      }, 15000);
      
      // Call the API with all required parameters
      const response = await auth.api.post('/api/v1/auth/reset-password', {
        token: formData.token,
        new_password: formData.new_password,
        confirm_password: formData.confirm_password,  
        language: navigator.language.split('-')[0] || 'en'
      });
      clearTimeout(timeoutId);
      
      // Extract data from response
      const { access_token, user } = response.data;
      
      if (access_token && user) {
        try {
          await auth.updateUserFromResetPassword(response.data);
          
          setStatus({
            type: 'success',
            message: 'Password reset successful. You are now logged in.'
          });
          
          // Redirect to dashboard after a short delay
          setTimeout(() => navigate('/'), 1500);
        } catch (authError) {
          setStatus({
            type: 'warning',
            message: 'Password reset successful but automatic login failed. Please log in with your new password.'
          });
          setTimeout(() => navigate('/login'), 2000);
        }
      } else {
        // Handle the case where we don't get expected data
        setStatus({
          type: 'success',
          message: 'Password has been successfully reset. Please log in with your new password.'
        });
        setTimeout(() => navigate('/login'), 2000);
      }
    } catch (error) {
      
      // Detailed error logging for debugging
      if (error.response) {
      } else if (error.request) {
      } else {
      }
      
      // Improved error handling to show the actual server error message
      let errorMessage;
      
      if (!navigator.onLine) {
        errorMessage = 'You appear to be offline. Please check your internet connection and try again.';
      } else if (error.response) {
        // Handle specific status codes
        switch (error.response.status) {
          case 400:
            errorMessage = 'Invalid request. Please check your information and try again.';
            break;
          case 401:
            errorMessage = 'Invalid or expired reset token. Please request a new password reset link.';
            break;
          case 404:
            errorMessage = 'The reset link is invalid or has already been used.';
            break;
          case 422:
            // Unprocessable entity - handle specific validation errors
            if (error.response.data?.detail) {
              if (Array.isArray(error.response.data.detail)) {
                // Handle FastAPI validation errors
                errorMessage = error.response.data.detail.map(err => 
                  err.msg || 'Validation error'
                ).join('. ');
              } else if (typeof error.response.data.detail === 'string') {
                errorMessage = error.response.data.detail;
              } else if (error.response.data.detail.message) {
                errorMessage = error.response.data.detail.message;
              } else {
                errorMessage = 'The server could not process your request. Please check your input.';
              }
            } else {
              errorMessage = 'The server could not process your request. Please check your input.';
            }
            break;
          case 500:
            errorMessage = 'A server error occurred. Please try again later or contact support.';
            break;
          default:
            // Extract the most specific error message available
            if (error.response.data?.detail?.message) {
              errorMessage = error.response.data.detail.message;
            } else if (error.response.data?.message) {
              errorMessage = error.response.data.message;
            } else if (error.response.data?.detail) {
              // Handle FastAPI's standard error format
              if (typeof error.response.data.detail === 'string') {
                errorMessage = error.response.data.detail;
              } else if (Array.isArray(error.response.data.detail)) {
                errorMessage = error.response.data.detail.map(err => 
                  `${err.loc ? err.loc.join('.') + ' - ' : ''}${err.msg}`
                ).join(', ');
              } else {
                errorMessage = JSON.stringify(error.response.data.detail);
              }
            } else {
              errorMessage = `Server error: ${error.response.status} ${error.response.statusText}`;
            }
        }
      } else if (error.request) {
        // Request was made but no response received
        errorMessage = 'No response received from server. Please check your connection and try again.';
      } else {
        // Request setup error
        errorMessage = error.message || 'An unknown error occurred';
      }
      
      setStatus({
        type: 'error',
        message: errorMessage || 'Failed to reset password. The link may have expired.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Calculate password strength
  const getPasswordStrength = (password) => {
    if (!password) return { score: 0, label: '', color: 'bg-gray-200' };
    
    let score = 0;
    if (password.length >= 8) score += 1;
    if (password.length >= 12) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/[a-z]/.test(password)) score += 1;
    if (/\d/.test(password)) score += 1;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score += 1;
    
    const strengthMap = [
      { label: 'Very Weak', color: 'bg-red-500' },
      { label: 'Weak', color: 'bg-orange-500' },
      { label: 'Fair', color: 'bg-yellow-500' },
      { label: 'Good', color: 'bg-blue-500' },
      { label: 'Strong', color: 'bg-green-500' },
      { label: 'Very Strong', color: 'bg-green-700' }
    ];
    
    return { 
      score, 
      ...strengthMap[Math.min(score, strengthMap.length - 1)]
    };
  };
  
  const strength = getPasswordStrength(formData.new_password);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
      <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900">Set new password</h2>
          <p className="text-gray-600 mt-2">
            Please enter your new password below
          </p>
        </div>
        
        {status.message && (
          <div className={`mb-6 p-4 rounded-md ${
            status.type === 'error' 
              ? 'bg-red-50 text-red-700 border border-red-200' 
              : status.type === 'warning'
              ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
              : 'bg-green-50 text-green-700 border border-green-200'
          }`}>
            <p>{status.message}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="new_password" className="block text-sm font-medium text-gray-700 mb-2">
              New Password
            </label>
            <div className="relative">
              <input
                id="new_password"
                name="new_password"
                type={showPassword ? "text" : "password"}
                value={formData.new_password}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-colors"
                placeholder="Enter new password"
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                {showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                )}
              </button>
            </div>
            
            {formData.new_password && (
              <>
                <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`${strength.color} h-2 rounded-full transition-all duration-300`} 
                    style={{ width: `${(strength.score / 6) * 100}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  Password strength: <span className="font-medium">{strength.label}</span>
                </p>
                <ul className="text-xs text-gray-600 mt-2 space-y-1">
                  <li className={`flex items-center ${formData.new_password.length >= 8 ? 'text-green-600' : 'text-gray-500'}`}>
                    <span className="mr-1">{formData.new_password.length >= 8 ? '✓' : '○'}</span> At least 8 characters
                  </li>
                  <li className={`flex items-center ${/[A-Z]/.test(formData.new_password) ? 'text-green-600' : 'text-gray-500'}`}>
                    <span className="mr-1">{/[A-Z]/.test(formData.new_password) ? '✓' : '○'}</span> Contains uppercase letters
                  </li>
                  <li className={`flex items-center ${/[a-z]/.test(formData.new_password) ? 'text-green-600' : 'text-gray-500'}`}>
                    <span className="mr-1">{/[a-z]/.test(formData.new_password) ? '✓' : '○'}</span> Contains lowercase letters
                  </li>
                  <li className={`flex items-center ${/\d/.test(formData.new_password) ? 'text-green-600' : 'text-gray-500'}`}>
                    <span className="mr-1">{/\d/.test(formData.new_password) ? '✓' : '○'}</span> Contains numbers
                  </li>
                  <li className={`flex items-center ${/[!@#$%^&*(),.?":{}|<>]/.test(formData.new_password) ? 'text-green-600' : 'text-gray-500'}`}>
                    <span className="mr-1">{/[!@#$%^&*(),.?":{}|<>]/.test(formData.new_password) ? '✓' : '○'}</span> Contains special characters
                  </li>
                </ul>
              </>
            )}
          </div>

          <div>
            <label htmlFor="confirm_password" className="block text-sm font-medium text-gray-700 mb-2">
              Confirm New Password
            </label>
            <div className="relative">
              <input
                id="confirm_password"
                name="confirm_password"
                type={showPassword ? "text" : "password"}
                value={formData.confirm_password}
                onChange={handleChange}
                required
                className={`w-full px-4 py-2 border ${
                  formData.confirm_password && formData.new_password !== formData.confirm_password
                    ? 'border-red-300 focus:ring-red-500'
                    : formData.confirm_password && formData.new_password === formData.confirm_password
                    ? 'border-green-300 focus:ring-green-500'
                    : 'border-gray-300 focus:ring-blue-500'
                } rounded-md focus:border-transparent outline-none transition-colors`}
                placeholder="Confirm new password"
              />
            </div>
            {formData.confirm_password && formData.new_password !== formData.confirm_password && (
              <p className="mt-1 text-sm text-red-600">Passwords do not match</p>
            )}
            {formData.confirm_password && formData.new_password === formData.confirm_password && (
              <p className="mt-1 text-sm text-green-600">Passwords match</p>
            )}
          </div>

          <button
            type="submit"
            disabled={isLoading || !formData.token}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Resetting Password...</span>
              </div>
            ) : "Reset Password"}
          </button>

          <button
            type="button"
            onClick={() => navigate('/login')}
            className="w-full text-gray-600 hover:text-gray-900 text-sm font-medium"
          >
            Back to login
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;